import { GGCoinError } from "./errors.ts";
import { streamTransactionsEvents, subscribeWalletEvents } from "./streaming.ts";
import type {
  AdminAccountResponse,
  AdminAuditListResponse,
  AdminGrowthResponse,
  AdminReversalResponse,
  AdminSessionResponse,
  AdminTransactionListResponse,
  ApiKeyListResponse,
  ApiKeyUsageView,
  ApiKeyView,
  ApprovalListResponse,
  ApprovalResolution,
  AuthorizeSpendRequest,
  AuthorizeSpendResponse,
  BalanceResponse,
  BuyCoinRequest,
  BuyCoinResponse,
  ConfirmTotpRequest,
  CreateSubWalletRequest,
  EnsureMasterWalletResponse,
  IssueApiKeyRequest,
  IssuedApiKey,
  LoginRequest,
  LoginResponse,
  MeResponse,
  MintAgentTokenRequest,
  MintedAgentToken,
  PendingApproval,
  PostTransactionRequest,
  PostTransactionResponse,
  RegisteredWebhookEndpoint,
  RegisterRequest,
  RegisterResponse,
  RegisterWebhookEndpointRequest,
  ResolvedApproval,
  ReverseTransactionRequest,
  ReverseTransactionResponse,
  SetWalletStatusRequest,
  SetWebhookEndpointStatusRequest,
  StartCheckoutRequest,
  StartCheckoutResponse,
  SubWalletResponse,
  TransactionEvent,
  TransactionHistoryParams,
  TransactionHistoryResponse,
  TransferRequest,
  TransferResponse,
  WalletEvent,
  WalletListItem,
  WalletStatusResponse,
  WebhookDeliveryListParams,
  WebhookDeliveryListResponse,
  WebhookEndpointListResponse,
  WebhookEndpointView,
} from "./types.ts";

/** Listener invoked for every event received over the SSE feed. */
export type TransactionEventListener = (event: TransactionEvent) => void;

/** Listener invoked for every event received over the WebSocket. */
export type WalletEventListener = (event: WalletEvent) => void;

/** Returned by streaming methods — calling it tears down the connection. */
export type Unsubscribe = () => Promise<void> | void;

/**
 * Constructor inputs for {@link GGCoinClient}. Every dependency the client
 * needs can be substituted (custom `fetch`, custom `EventSource`,
 * pre-existing token) so a single class works for browser, Bun, Node 18+,
 * and tests.
 */
export interface GGCoinClientConfig {
  /** HTTPS base URL of the api, e.g. `https://api.ggcoin.ai`. No trailing slash. */
  baseUrl: string;
  /** Optional bearer token — skips the login step if you already have one. */
  jwt?: string;
  /**
   * Optional programmatic API key (#131). When set, operational requests send
   * `x-api-key` instead of a bearer token — the server-to-server auth mode for
   * the Platform SDK (no user login). Subject to the key's scopes; takes
   * precedence over `jwt` when both are present.
   */
  apiKey?: string;
  /**
   * Proxy-authenticated mode (ggcoin.ai#227). When true, authenticated requests
   * are sent WITHOUT a bearer/api-key — a trusted reverse proxy (the admin
   * oauth2-proxy) authenticates the caller and forwards the identity to the API.
   * Same-origin cookies ride along (`credentials: "include"`). Use only behind
   * such a proxy; never point a proxy-auth client at the public API.
   */
  proxyAuth?: boolean;
  /**
   * Called when a `proxyAuth` request comes back `401` — i.e. the reverse
   * proxy's session expired (ggcoin.ai#227). Because the SDK sends
   * `Accept: application/json`, oauth2-proxy answers an expired session with a
   * same-origin `401` instead of a cross-origin redirect (which a browser
   * `fetch` would surface as an opaque network error). The admin console wires
   * this to bounce the operator through `/oauth2/sign_in` for a fresh SSO login.
   */
  onSessionExpired?: () => void;
  /** Custom fetch impl — defaults to `globalThis.fetch`. */
  fetch?: typeof fetch;
  /** Custom EventSource ctor — defaults to `globalThis.EventSource`. */
  EventSource?: typeof EventSource;
  /** Custom WebSocket ctor — defaults to `globalThis.WebSocket`. */
  WebSocket?: typeof WebSocket;
}

/**
 * Typed client for the GG-Coin api. Every method maps 1:1 to an HTTP route;
 * the only stateful piece is the bearer token, which `login` writes and
 * `logout` clears. Streaming methods open SSE / WS connections and return an
 * `Unsubscribe` for cleanup.
 *
 * @example
 *   const client = new GGCoinClient({ baseUrl: "http://localhost:3001" });
 *   await client.login({ email, password, totpCode });
 *   const balance = await client.getBalance(walletId);
 */
export class GGCoinClient {
  /** Current bearer token; `null` when no session has been established. */
  private jwt: string | null;
  /** Programmatic API key for server-to-server auth (#131); `null` when unset. */
  private readonly apiKey: string | null;
  /** Proxy-auth mode (#227): a trusted reverse proxy authenticates; send no bearer. */
  private readonly proxyAuth: boolean;
  /** Invoked on a `proxyAuth` `401` (reverse-proxy session expired); `null` when unset. */
  private readonly onSessionExpired: (() => void) | null;
  /** Concrete fetch — resolved once at construct time. */
  private readonly fetchImpl: typeof fetch;
  /** Concrete EventSource ctor (may be undefined in non-browser runtimes). */
  private readonly EventSourceImpl: typeof EventSource | undefined;
  /** Concrete WebSocket ctor (may be undefined in non-browser runtimes). */
  private readonly WebSocketImpl: typeof WebSocket | undefined;
  /** Normalized api base URL — no trailing slash. */
  private readonly baseUrl: string;

  /**
   * Wire dependencies from the {@link GGCoinClientConfig}. Normalizes the
   * base URL, resolves the runtime primitives (`fetch` / `EventSource` /
   * `WebSocket`) from the config or the global scope, and stores the
   * optional pre-existing token.
   */
  constructor(config: GGCoinClientConfig) {
    if (!config.baseUrl) throw new Error("GGCoinClient: baseUrl is required");
    // Strip a single trailing slash so callers can pass either form without
    // producing `//path` URLs that some proxies refuse.
    this.baseUrl = config.baseUrl.replace(/\/$/, "");
    this.jwt = config.jwt ?? null;
    this.apiKey = config.apiKey ?? null;
    this.proxyAuth = config.proxyAuth ?? false;
    this.onSessionExpired = config.onSessionExpired ?? null;
    this.fetchImpl =
      config.fetch ??
      // `fetch` rejects with TypeError if `this` isn't `globalThis`; bind to be safe.
      (globalThis.fetch
        ? globalThis.fetch.bind(globalThis)
        : (() => {
            throw new Error("GGCoinClient: no fetch impl and globalThis.fetch is missing");
          })());
    this.EventSourceImpl = config.EventSource ?? globalThis.EventSource;
    this.WebSocketImpl = config.WebSocket ?? globalThis.WebSocket;
  }

  /** Current bearer token, or `null` when no session is held. */
  get token(): string | null {
    return this.jwt;
  }

  /** Override the held token — useful for SSR token rehydration. */
  setToken(jwt: string | null): void {
    this.jwt = jwt;
  }

  // =========== Auth ============

  /** POST /auth/register — creates the account, returns TOTP secret + URI. */
  async register(input: RegisterRequest): Promise<RegisterResponse> {
    return this.request<RegisterResponse>("POST", "/auth/register", input, false);
  }

  /** POST /auth/confirm-totp — completes 2FA enrollment with the first valid code. */
  async confirmTotp(input: ConfirmTotpRequest): Promise<void> {
    await this.request<null>("POST", "/auth/confirm-totp", input, false);
  }

  /** POST /auth/login — verifies password + TOTP, captures the JWT internally. */
  async login(input: LoginRequest): Promise<LoginResponse> {
    const res = await this.request<LoginResponse>("POST", "/auth/login", input, false);
    this.jwt = res.jwt;
    return res;
  }

  /** POST /auth/logout — server revokes the session, client drops the token. */
  async logout(): Promise<void> {
    await this.request<null>("POST", "/auth/logout", null, true);
    this.jwt = null;
  }

  /** GET /me — quick "who am I" probe. Needs an active token. */
  async me(): Promise<MeResponse> {
    return this.request<MeResponse>("GET", "/me", undefined, true);
  }

  // =========== Wallets ============

  /**
   * POST /wallets — idempotently ensure the session's account owns a master
   * wallet. Returns the existing wallet (with `created: false`) on repeat
   * calls. Safe to invoke unconditionally on first Dashboard render.
   */
  async ensureMasterWallet(): Promise<EnsureMasterWalletResponse> {
    return this.request<EnsureMasterWalletResponse>("POST", "/wallets", null, true);
  }

  /**
   * GET /wallets — list the master wallets the session account owns. Returns
   * an empty array when the account has none yet.
   */
  async listWallets(): Promise<WalletListItem[]> {
    return this.request<WalletListItem[]>("GET", "/wallets", undefined, true);
  }

  /** POST /wallets/sub-wallets — create a sub-wallet under a master. */
  async createSubWallet(input: CreateSubWalletRequest): Promise<SubWalletResponse> {
    return this.request<SubWalletResponse>("POST", "/wallets/sub-wallets", input, true);
  }

  /**
   * GET /wallets/:walletId/sub-wallets — list the sub-wallets under a master.
   * Returns an empty array when the wallet has none (or isn't on this account).
   */
  async listSubWallets(walletId: string): Promise<SubWalletResponse[]> {
    return this.request<SubWalletResponse[]>(
      "GET",
      `/wallets/${encodeURIComponent(walletId)}/sub-wallets`,
      undefined,
      true,
    );
  }

  /** PATCH /wallets/:walletId/status — freeze / unfreeze a master wallet. */
  async setWalletStatus(
    walletId: string,
    body: SetWalletStatusRequest,
  ): Promise<WalletStatusResponse> {
    return this.request<WalletStatusResponse>(
      "PATCH",
      `/wallets/${encodeURIComponent(walletId)}/status`,
      body,
      true,
    );
  }

  /**
   * GET /wallets/:walletId/balance — canonical signed balance from the ledger.
   * Optional `subWalletId` narrows to a single sub-wallet.
   */
  async getBalance(
    walletId: string,
    opts: { subWalletId?: string } = {},
  ): Promise<BalanceResponse> {
    const qs = opts.subWalletId ? `?sub_wallet_id=${encodeURIComponent(opts.subWalletId)}` : "";
    return this.request<BalanceResponse>(
      "GET",
      `/wallets/${encodeURIComponent(walletId)}/balance${qs}`,
      undefined,
      true,
    );
  }

  // =========== Transactions ============

  /**
   * GET /transactions — paginated, newest-first account history. Pass
   * `walletId` to narrow to one master, and `before` (a prior `nextBefore`)
   * with `limit` to page back through older rows.
   */
  async listTransactions(
    params: TransactionHistoryParams = {},
  ): Promise<TransactionHistoryResponse> {
    const qs = new URLSearchParams();
    if (params.walletId) qs.set("wallet_id", params.walletId);
    if (params.before) qs.set("before", params.before);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
    return this.request<TransactionHistoryResponse>(
      "GET",
      `/transactions${suffix}`,
      undefined,
      true,
    );
  }

  /**
   * POST /transactions/buy — closed-loop on-ramp: mint coin into a sub-wallet
   * against a confirmed payment (#26). `idempotencyKey` dedups the mint, so a
   * replayed call returns the original transaction instead of minting twice.
   */
  async buyCoin(input: BuyCoinRequest): Promise<BuyCoinResponse> {
    return this.request<BuyCoinResponse>("POST", "/transactions/buy", input, true);
  }

  /**
   * POST /payments/:gateway/checkout — start a hosted checkout (#150). Returns a
   * `redirectUrl` to send the buyer's browser to; coin is minted server-side
   * only on the gateway's verified capture callback. `gateway` defaults to
   * `"ccbill"`. The buyer's account is bound from the session, not the request.
   */
  async startCheckout(input: StartCheckoutRequest): Promise<StartCheckoutResponse> {
    const gateway = input.gateway ?? "ccbill";
    return this.request<StartCheckoutResponse>(
      "POST",
      `/payments/${encodeURIComponent(gateway)}/checkout`,
      { walletId: input.walletId, subWalletId: input.subWalletId, amount: input.amount },
      true,
    );
  }

  /**
   * POST /transactions/transfer — P2P (#128): move coin from your sub-wallet to
   * a recipient's master wallet. Returns a discriminated outcome — `posted`,
   * `require-approval` (above threshold; an approval is parked), or `reject`.
   * `idempotencyKey` dedups, so a replay returns the original transfer.
   */
  async transfer(input: TransferRequest): Promise<TransferResponse> {
    return this.request<TransferResponse>("POST", "/transactions/transfer", input, true);
  }

  /** POST /transactions/authorize — discriminated `SpendDecision`. */
  async authorizeSpend(input: AuthorizeSpendRequest): Promise<AuthorizeSpendResponse> {
    return this.request<AuthorizeSpendResponse>("POST", "/transactions/authorize", input, true);
  }

  /** POST /transactions/post — write a validated double-entry posting. */
  async postTransaction(input: PostTransactionRequest): Promise<PostTransactionResponse> {
    return this.request<PostTransactionResponse>("POST", "/transactions/post", input, true);
  }

  /**
   * POST /transactions/:id/reverse — net out a posted transaction with a new
   * opposite-direction posting. URL `transactionId` is authoritative.
   */
  async reverseTransaction(
    transactionId: string,
    body: ReverseTransactionRequest = {},
  ): Promise<ReverseTransactionResponse> {
    return this.request<ReverseTransactionResponse>(
      "POST",
      `/transactions/${encodeURIComponent(transactionId)}/reverse`,
      body,
      true,
    );
  }

  // =========== Approvals (#29) ============

  /**
   * GET /approvals — the master's inbox of above-threshold spends awaiting a
   * verdict (oldest-first), account-scoped to the authenticated master.
   */
  async getPendingApprovals(): Promise<PendingApproval[]> {
    const res = await this.request<ApprovalListResponse>("GET", "/approvals", undefined, true);
    return res.approvals;
  }

  /** POST /approvals/:approvalId/resolve — approve or deny a pending request. */
  async resolveApproval(
    approvalId: string,
    resolution: ApprovalResolution,
  ): Promise<ResolvedApproval> {
    return this.request<ResolvedApproval>(
      "POST",
      `/approvals/${encodeURIComponent(approvalId)}/resolve`,
      { resolution },
      true,
    );
  }

  // =========== Agent tokens (MCP) ============

  /**
   * POST /mcp/tokens — mint an MCP agent token bound to one sub-wallet. The
   * `token` is returned EXACTLY once; hand it to the agent now (it's never
   * recoverable). A compromise leaks only that sub-wallet.
   */
  async mintAgentToken(input: MintAgentTokenRequest): Promise<MintedAgentToken> {
    return this.request<MintedAgentToken>("POST", "/mcp/tokens", input, true);
  }

  // =========== API keys (#32) ============

  /**
   * POST /api-keys — self-issue an API key for the session account. The
   * plaintext `key` is returned EXACTLY once; surface it to the owner now,
   * it is never recoverable.
   */
  async issueApiKey(input: IssueApiKeyRequest): Promise<IssuedApiKey> {
    return this.request<IssuedApiKey>("POST", "/api-keys", input, true);
  }

  /**
   * GET /api-keys — list the account's keys (management view, no secrets),
   * each with a usage snapshot (#31): requests served so far today vs quota.
   */
  async listApiKeys(): Promise<ApiKeyUsageView[]> {
    const res = await this.request<ApiKeyListResponse>("GET", "/api-keys", undefined, true);
    return res.apiKeys;
  }

  /**
   * PATCH /api-keys/:keyId/quota — set or clear a key's per-UTC-day request
   * quota (#32). Pass `null` to remove the cap (unlimited).
   */
  async updateApiKeyQuota(keyId: string, quotaPerDay: number | null): Promise<ApiKeyView> {
    return this.request<ApiKeyView>(
      "PATCH",
      `/api-keys/${encodeURIComponent(keyId)}/quota`,
      { quotaPerDay },
      true,
    );
  }

  /** POST /api-keys/:keyId/revoke — revoke a key the account owns (idempotent). */
  async revokeApiKey(keyId: string): Promise<ApiKeyView> {
    return this.request<ApiKeyView>(
      "POST",
      `/api-keys/${encodeURIComponent(keyId)}/revoke`,
      null,
      true,
    );
  }

  /**
   * POST /api-keys/:keyId/rotate — mint a replacement carrying the key's policy
   * (label + scopes + expiry) and revoke the old one. The new plaintext `key`
   * is returned EXACTLY once; swap it into the integration immediately.
   */
  async rotateApiKey(keyId: string): Promise<IssuedApiKey> {
    return this.request<IssuedApiKey>(
      "POST",
      `/api-keys/${encodeURIComponent(keyId)}/rotate`,
      null,
      true,
    );
  }

  // =========== Webhook endpoints (#25) ============

  /**
   * POST /webhooks/endpoints — register an outbound endpoint. The `secret`
   * (used to verify our HMAC signatures) is returned EXACTLY once.
   */
  async registerWebhookEndpoint(
    input: RegisterWebhookEndpointRequest,
  ): Promise<RegisteredWebhookEndpoint> {
    return this.request<RegisteredWebhookEndpoint>("POST", "/webhooks/endpoints", input, true);
  }

  /** GET /webhooks/endpoints — list the account's endpoints (no secrets). */
  async listWebhookEndpoints(): Promise<WebhookEndpointView[]> {
    const res = await this.request<WebhookEndpointListResponse>(
      "GET",
      "/webhooks/endpoints",
      undefined,
      true,
    );
    return res.endpoints;
  }

  /** GET /webhooks/deliveries — the account's delivery log, newest-first. */
  async listWebhookDeliveries(
    params: WebhookDeliveryListParams = {},
  ): Promise<WebhookDeliveryListResponse> {
    const qs = new URLSearchParams();
    if (params.before) qs.set("before", params.before);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
    return this.request<WebhookDeliveryListResponse>(
      "GET",
      `/webhooks/deliveries${suffix}`,
      undefined,
      true,
    );
  }

  /** PATCH /webhooks/endpoints/:id — enable or disable an endpoint. */
  async setWebhookEndpointStatus(
    id: string,
    input: SetWebhookEndpointStatusRequest,
  ): Promise<WebhookEndpointView> {
    return this.request<WebhookEndpointView>(
      "PATCH",
      `/webhooks/endpoints/${encodeURIComponent(id)}`,
      input,
      true,
    );
  }

  /** DELETE /webhooks/endpoints/:id — remove an endpoint (404 if not the account's). */
  async deleteWebhookEndpoint(id: string): Promise<void> {
    await this.request<null>(
      "DELETE",
      `/webhooks/endpoints/${encodeURIComponent(id)}`,
      undefined,
      true,
    );
  }

  // =========== Admin / operator console (#119) — staff-gated ============

  /**
   * GET /admin/session — the signed-in operator's identity + an RP-initiated
   * logout URL (#227). The admin console has no local session (the oauth2-proxy
   * authenticates); this is how it learns who it is and how to sign out. A null
   * `logoutUrl` means logout isn't configured — clear the proxy cookie only.
   */
  async getAdminSession(): Promise<AdminSessionResponse> {
    return this.request<AdminSessionResponse>("GET", "/admin/session", undefined, true);
  }

  /** GET /admin/accounts/:id — account profile + its master wallets. */
  async getAdminAccount(accountId: string): Promise<AdminAccountResponse> {
    return this.request<AdminAccountResponse>(
      "GET",
      `/admin/accounts/${encodeURIComponent(accountId)}`,
      undefined,
      true,
    );
  }

  /** GET /admin/accounts/:id/transactions — that account's history. */
  async listAdminAccountTransactions(
    accountId: string,
    params: { before?: string; limit?: number } = {},
  ): Promise<AdminTransactionListResponse> {
    const qs = new URLSearchParams();
    if (params.before) qs.set("before", params.before);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
    return this.request<AdminTransactionListResponse>(
      "GET",
      `/admin/accounts/${encodeURIComponent(accountId)}/transactions${suffix}`,
      undefined,
      true,
    );
  }

  /** GET /admin/accounts/:id/webhook-deliveries — that account's delivery log. */
  async listAdminAccountDeliveries(
    accountId: string,
    params: { before?: string; limit?: number } = {},
  ): Promise<WebhookDeliveryListResponse> {
    const qs = new URLSearchParams();
    if (params.before) qs.set("before", params.before);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
    return this.request<WebhookDeliveryListResponse>(
      "GET",
      `/admin/accounts/${encodeURIComponent(accountId)}/webhook-deliveries${suffix}`,
      undefined,
      true,
    );
  }

  /** POST /admin/wallets/:walletId/status — operator freeze/unfreeze on a target
   * account. An optional `reason` is recorded in the audit trail (#152). */
  async setAdminWalletStatus(
    walletId: string,
    input: { accountId: string; status: "active" | "frozen"; reason?: string },
  ): Promise<{ id: string; status: string; balance: string }> {
    return this.request(
      "POST",
      `/admin/wallets/${encodeURIComponent(walletId)}/status`,
      input,
      true,
    );
  }

  /** POST /admin/transactions/:id/reverse — operator reversal/refund on a target
   * account. An optional `reason` is recorded in the audit trail (#152). */
  async reverseAdminTransaction(
    transactionId: string,
    input: { accountId: string; reason?: string },
  ): Promise<AdminReversalResponse> {
    return this.request<AdminReversalResponse>(
      "POST",
      `/admin/transactions/${encodeURIComponent(transactionId)}/reverse`,
      input,
      true,
    );
  }

  /** GET /admin/audit — global operator-action trail, newest-first (#152). */
  async listAdminAudit(
    params: { before?: string; limit?: number } = {},
  ): Promise<AdminAuditListResponse> {
    const qs = new URLSearchParams();
    if (params.before) qs.set("before", params.before);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
    return this.request<AdminAuditListResponse>("GET", `/admin/audit${suffix}`, undefined, true);
  }

  /** GET /admin/growth — signup → first-key → first-transaction funnel (#37). */
  async getAdminGrowth(): Promise<AdminGrowthResponse> {
    return this.request<AdminGrowthResponse>("GET", "/admin/growth", undefined, true);
  }

  /** GET /admin/accounts/:id/audit — operator actions affecting one account (#152). */
  async listAdminAccountAudit(
    accountId: string,
    params: { before?: string; limit?: number } = {},
  ): Promise<AdminAuditListResponse> {
    const qs = new URLSearchParams();
    if (params.before) qs.set("before", params.before);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
    return this.request<AdminAuditListResponse>(
      "GET",
      `/admin/accounts/${encodeURIComponent(accountId)}/audit${suffix}`,
      undefined,
      true,
    );
  }

  // =========== Streaming ============

  /**
   * Open the SSE transactions feed. `onEvent` fires for every posted /
   * reversed event on the per-account channel. Returns an `Unsubscribe`
   * — call it to close the connection.
   */
  streamTransactions(opts: {
    onEvent: TransactionEventListener;
    onError?: (err: Error) => void;
  }): Unsubscribe {
    this.requireToken();
    return streamTransactionsEvents({
      baseUrl: this.baseUrl,
      token: this.jwt!,
      EventSourceImpl: this.EventSourceImpl,
      onEvent: opts.onEvent,
      onError: opts.onError,
    });
  }

  /**
   * Open the wallet WebSocket feed for balance + approval push events. Resolves
   * once the socket is open; the returned `Unsubscribe` closes it.
   */
  async subscribeWallet(opts: {
    onEvent: WalletEventListener;
    onError?: (err: Error) => void;
  }): Promise<Unsubscribe> {
    this.requireToken();
    return subscribeWalletEvents({
      baseUrl: this.baseUrl,
      token: this.jwt!,
      WebSocketImpl: this.WebSocketImpl,
      onEvent: opts.onEvent,
      onError: opts.onError,
    });
  }

  // =========== Internals ============

  /**
   * Guard for routes that must run authenticated. Throws a typed
   * `UNAUTHENTICATED` `GGCoinError` (no HTTP round-trip) so callers can
   * `catch` the same way they would for a server-side 401.
   */
  private requireToken(): void {
    if (!this.jwt) {
      throw new GGCoinError("UNAUTHENTICATED", 401, "no token; call login() first");
    }
  }

  /**
   * Generic HTTP wrapper. Serializes JSON, attaches bearer when needed,
   * translates `{ error: { code, message } }` responses into typed
   * {@link GGCoinError}s, returns the response body cast as `T`.
   */
  private async request<T>(
    method: string,
    path: string,
    body: unknown,
    requireAuth: boolean,
  ): Promise<T> {
    // Always advertise a JSON preference. Beyond matching what we parse, this
    // makes a reverse proxy (the admin oauth2-proxy, #227) return a same-origin
    // `401` for an expired session instead of a cross-origin login redirect that
    // a browser `fetch` can only surface as an opaque network error.
    const headers: Record<string, string> = { accept: "application/json" };
    const hasBody = body !== undefined && body !== null;
    if (hasBody) headers["content-type"] = "application/json";
    const init: RequestInit = { method, headers };
    if (requireAuth) {
      // Programmatic API key (#131) takes precedence — server-to-server mode.
      if (this.apiKey) {
        headers["x-api-key"] = this.apiKey;
      } else if (this.proxyAuth) {
        // #227: a trusted reverse proxy authenticates and forwards the identity —
        // send no bearer, just let the same-origin proxy session cookie ride along.
        init.credentials = "include";
      } else {
        this.requireToken();
        headers.authorization = `Bearer ${this.jwt}`;
      }
    }
    if (hasBody) init.body = JSON.stringify(body);

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}${path}`, init);
    } catch (err) {
      throw new GGCoinError(
        "NETWORK_ERROR",
        0,
        err instanceof Error ? err.message : "network error",
      );
    }
    // A `401` under proxy-auth means the reverse proxy's own session lapsed
    // (the API itself never 401s an admin call — it 403s a non-staff one). Give
    // the app a chance to re-run the SSO login before the error propagates.
    if (res.status === 401 && this.proxyAuth && this.onSessionExpired) {
      this.onSessionExpired();
    }
    if (!res.ok) {
      let code = "INTERNAL_ERROR";
      let message = `HTTP ${res.status}`;
      try {
        const errBody = (await res.json()) as
          | { error?: { code?: string; message?: string } }
          | undefined;
        if (errBody?.error?.code) code = errBody.error.code;
        if (errBody?.error?.message) message = errBody.error.message;
      } catch {
        // Body wasn't JSON; keep the generic message.
      }
      throw new GGCoinError(code, res.status, message);
    }
    if (res.status === 204) return null as T;
    return (await res.json()) as T;
  }
}
